import { Fragment, type ReactNode } from "react";
import { Dialog, Transition } from "@headlessui/react";
import IconCloseX from "~/components/icons/IconCloseX";
import { cn } from "./utils/cn";
import IconButton from "./IconButton";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Side the drawer slides in from (logical). Defaults to `end`. */
  side?: "start" | "end";
  title?: ReactNode;
  footer?: ReactNode;
  hideClose?: boolean;
  /** Tailwind max-width class for the panel. */
  widthClassName?: string;
  panelClassName?: string;
  /** Extra classes for the scrollable body region. */
  bodyClassName?: string;
  /** Extra classes for the sticky footer region. */
  footerClassName?: string;
  ["data-cy"]?: string;
}

/**
 * Side panel that shares the design system's surface, header, and footer
 * conventions with ui/Modal — one consistent overlay family. Slides from the
 * logical end (or start) edge; full-height; sticky header + footer.
 */
const Drawer = ({
  open,
  onClose,
  children,
  side = "end",
  title,
  footer,
  hideClose,
  widthClassName = "max-w-md",
  panelClassName,
  bodyClassName,
  footerClassName,
  ...props
}: DrawerProps) => {
  const showHeader = Boolean(title) || !hideClose;
  // Logical end = inline-end edge; in RTL that's the left, in LTR the right.
  const sideClass = side === "end" ? "ms-auto" : "me-auto";
  const enterFrom =
    side === "end"
      ? "translate-x-full rtl:-translate-x-full"
      : "-translate-x-full rtl:translate-x-full";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-modal" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out-soft duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="transition-opacity fixed inset-x-0 bottom-0 top-[var(--navbar-offset,68px)] bg-menu/30 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-x-0 bottom-0 top-[var(--navbar-offset,68px)] overflow-hidden">
          <div className="flex h-full">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out-soft duration-300"
              enterFrom={enterFrom}
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo={enterFrom}
            >
              <Dialog.Panel
                data-cy={props["data-cy"]}
                className={cn(
                  "flex h-full w-full flex-col overflow-hidden bg-surface text-start text-menu shadow-e4",
                  side === "end" ? "rounded-s-modal" : "rounded-e-modal",
                  sideClass,
                  widthClassName,
                  panelClassName
                )}
              >
                {showHeader && (
                  <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-4 sm:px-8 sm:pt-6">
                    {title ? (
                      <Dialog.Title className="min-w-0 flex-1 text-lg font-semibold text-menu">
                        {title}
                      </Dialog.Title>
                    ) : (
                      <span aria-hidden="true" />
                    )}
                    {!hideClose && (
                      <IconButton
                        aria-label="Close"
                        size="sm"
                        variant="ghost"
                        icon={<IconCloseX />}
                        onClick={onClose}
                        className="-me-1 -mt-1 shrink-0"
                      />
                    )}
                  </div>
                )}
                <div
                  className={cn(
                    "flex-1 overflow-y-auto px-5 pt-4 sm:px-8",
                    // Absorb the Android-app bottom buffer (0px on web/iOS).
                    footer
                      ? "pb-4"
                      : "pb-[calc(1rem+var(--app-bottom-buffer,0px))]",
                    bodyClassName
                  )}
                >
                  {children}
                </div>
                {footer && (
                  <div
                    className={cn(
                      "flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border-subtle px-5 pb-[calc(0.875rem+var(--app-bottom-buffer,0px))] pt-3.5 sm:px-8",
                      footerClassName
                    )}
                  >
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Drawer;
